/** Parse the address field from an SDP ICE candidate line. */
export function candidateAddress(line: string): string | null {
  if (!line.includes(' typ ')) return null;
  const parts = line.trim().split(/\s+/);
  const typIdx = parts.indexOf('typ');
  if (typIdx < 2) return null;
  return parts[typIdx - 2] ?? null;
}

export function isLinkLocalIpv6(addr: string): boolean {
  const a = addr.toLowerCase();
  return a.startsWith('fe80:') || a.includes(' fe80:');
}

/** Global/ULA IPv6 (excludes link-local fe80 — used for srflx delay buckets). */
export function isIpv6IceLine(line: string): boolean {
  const addr = candidateAddress(line);
  if (!addr) return false;
  if (!addr.includes(':')) return false;
  return !isLinkLocalIpv6(addr);
}

/** Any IPv6 host candidate, including fe80 — best for same-LAN Wi‑Fi/Ethernet. */
export function isIpv6HostLine(line: string): boolean {
  if (!/\styp host\b/.test(line)) return false;
  const addr = candidateAddress(line);
  return Boolean(addr?.includes(':'));
}

export function isIpv4IceLine(line: string): boolean {
  const addr = candidateAddress(line);
  if (!addr) return false;
  return addr.includes('.') && !addr.includes(':');
}

export function isRelayLine(line: string): boolean {
  return /\styp relay\b/.test(line);
}

export function isSrflxLine(line: string): boolean {
  return /\styp srflx\b/.test(line);
}

export interface IceForwardDelays {
  ipv4HostMs: number;
  srflxIpv4Ms: number;
  srflxIpv6Ms: number;
}

type HoldBucket = 'ipv4Host' | 'srflxIpv4' | 'srflxIpv6';

/**
 * When ICE_PREFER_IPV6: IPv6 host + relay sent immediately; optional delays on
 * IPv4 host and srflx (v4/v6 separate). On connected, held candidates are
 * discarded (not signaled) to avoid mid-session ICE renomination.
 */
export class IceCandidateForwarder {
  private readonly holds: Record<HoldBucket, RTCIceCandidateInit[]> = {
    ipv4Host: [],
    srflxIpv4: [],
    srflxIpv6: [],
  };
  private readonly timers: Partial<Record<HoldBucket, number>> = {};
  private abandoned = false;

  constructor(
    private readonly send: (c: RTCIceCandidateInit) => void,
    private readonly preferIpv6: boolean,
    private readonly delays: IceForwardDelays
  ) {}

  /** Call when local ICE gathering produces a candidate (or end-of-candidates). */
  handle(candidate: RTCIceCandidateInit): void {
    if (!this.preferIpv6) {
      this.send(candidate);
      return;
    }
    const line = candidate.candidate ?? '';
    if (!line) {
      this.flush();
      return;
    }
    if (this.abandoned) {
      if (this.wouldHold(line)) return;
      this.send(candidate);
      return;
    }
    if (isRelayLine(line)) {
      this.send(candidate);
      return;
    }
    if (isIpv6HostLine(line)) {
      this.send(candidate);
      return;
    }
    if (isSrflxLine(line)) {
      if (isIpv6IceLine(line)) {
        this.holdOrSend('srflxIpv6', candidate, this.delays.srflxIpv6Ms);
        return;
      }
      if (isIpv4IceLine(line)) {
        this.holdOrSend('srflxIpv4', candidate, this.delays.srflxIpv4Ms);
        return;
      }
      this.send(candidate);
      return;
    }
    if (isIpv4IceLine(line)) {
      this.holdOrSend('ipv4Host', candidate, this.delays.ipv4HostMs);
      return;
    }
    this.send(candidate);
  }

  /** Release all held candidates (end-of-gathering while still negotiating). */
  flush(): void {
    if (this.abandoned) return;
    for (const bucket of Object.keys(this.holds) as HoldBucket[]) {
      this.releaseBucket(bucket);
    }
  }

  /** Drop held candidates once the peer connection is up (do not signal them). */
  discardHeld(): void {
    this.abandoned = true;
    for (const bucket of Object.keys(this.timers) as HoldBucket[]) {
      const t = this.timers[bucket];
      if (t !== undefined) {
        window.clearTimeout(t);
        delete this.timers[bucket];
      }
    }
    for (const bucket of Object.keys(this.holds) as HoldBucket[]) {
      this.holds[bucket].length = 0;
    }
  }

  destroy(): void {
    this.discardHeld();
  }

  private wouldHold(line: string): boolean {
    if (isRelayLine(line) || isIpv6HostLine(line)) return false;
    if (isSrflxLine(line)) {
      if (isIpv6IceLine(line)) return this.delays.srflxIpv6Ms > 0;
      if (isIpv4IceLine(line)) return this.delays.srflxIpv4Ms > 0;
    }
    if (isIpv4IceLine(line) && !isSrflxLine(line)) return this.delays.ipv4HostMs > 0;
    return false;
  }

  private holdOrSend(bucket: HoldBucket, candidate: RTCIceCandidateInit, delayMs: number): void {
    if (delayMs <= 0) {
      this.send(candidate);
      return;
    }
    this.holds[bucket].push(candidate);
    this.scheduleRelease(bucket, delayMs);
  }

  private scheduleRelease(bucket: HoldBucket, delayMs: number): void {
    if (this.abandoned || this.timers[bucket] !== undefined) return;
    this.timers[bucket] = window.setTimeout(() => {
      delete this.timers[bucket];
      this.releaseBucket(bucket);
    }, delayMs);
  }

  private releaseBucket(bucket: HoldBucket): void {
    if (this.abandoned) {
      this.holds[bucket].length = 0;
      return;
    }
    for (const c of this.holds[bucket]) this.send(c);
    this.holds[bucket].length = 0;
  }
}
